import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timer } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { getLocalRoutineById } from '../../db/operations.js';
import { clearPersistedHud, readPersistedHud } from '../../stores/routineStore.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

interface ResumeInfo {
  routineId: string;
  routineTitle: string;
  blockNumber: number;
  blockCount: number;
}

/**
 * Offers to continue a Focus HUD session that was interrupted (e.g. Android killed the app).
 * The session itself is saved by routineStore; this card only reads it, so nothing starts automatically.
 */
export const ResumeHudCard: React.FC = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState<ResumeInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    const saved = readPersistedHud();
    if (!saved) return;
    getLocalRoutineById(saved.routineId)
      .then((routine) => {
        if (cancelled) return;
        if (!routine || routine.deletedAt || saved.blockIndex >= routine.blocks.length) {
          clearPersistedHud();
          return;
        }
        setInfo({
          routineId: routine.id,
          routineTitle: routine.title,
          blockNumber: saved.blockIndex + 1,
          blockCount: routine.blocks.length
        });
      })
      .catch(() => {
        // Dexie unavailable: simply don't offer to resume
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info) return null;

  const handleResume = () => navigate(`/routines/${info.routineId}/hud`);
  const handleDiscard = () => {
    clearPersistedHud();
    setInfo(null);
  };

  return (
    <section
      aria-label="جلسهٔ تمرین نیمه‌تمام"
      data-testid="hud-resume-card"
      className="bg-base-200 border border-primary/40 rounded-2xl md:rounded-[20px] p-3.5 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="size-12 shrink-0 rounded-xl bg-neutral text-primary-soft flex items-center justify-center">
          <Timer className="size-5" />
        </span>
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[11px] font-semibold text-primary-soft">جلسهٔ تمرین نیمه‌تمام</span>
          <span className="text-[15px] font-semibold text-base-content truncate" data-testid="hud-resume-title">
            {info.routineTitle}
          </span>
          <span className="text-xs text-base-content/60">
            بلوک {toPersianDigits(info.blockNumber)} از {toPersianDigits(info.blockCount)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 md:shrink-0">
        <Button
          variant="primary"
          size="md"
          onClick={handleResume}
          data-testid="hud-resume-button"
          className="flex-1 md:flex-none"
        >
          ادامهٔ جلسه
        </Button>
        <Button
          variant="ghost"
          size="md"
          onClick={handleDiscard}
          data-testid="hud-resume-discard"
          className="flex-1 md:flex-none"
        >
          کنار گذاشتن
        </Button>
      </div>
    </section>
  );
};

ResumeHudCard.displayName = 'ResumeHudCard';
